
import PropTypes from "prop-types";

/**
 * StatusBadge — badge de estado del design system.
 *
 * El significado nunca depende sólo del color: siempre acompaña un texto y,
 * opcionalmente, un icono, para cumplir contraste y accesibilidad.
 *
 * Props:
 *   label   — texto visible (obligatorio)
 *   tone    — "neutral" (default) | "info" | "success" | "warning" | "error"
 *   icon    — nodo React opcional a la izquierda
 *   size    — "xs" | "sm" (default) | "md" | "lg"
 *   outline — variante con borde en vez de relleno
 *   title   — tooltip nativo con la explicación larga del estado
 */

const TONE = {
    neutral: "badge-ghost",
    info: "badge-info",
    success: "badge-success",
    warning: "badge-warning",
    error: "badge-error",
};

// En outline el texto conserva `base-content` para garantizar contraste AA en
// ambos temas (el amarillo de `warning` sobre base clara no lo alcanzaba). El
// color semántico lo llevan el borde y el icono, igual que hace `badge-outline`
// de DaisyUI en el resto del design system.
// `badge-outline` de DaisyUI fija `border-color: currentColor`, así que ata el
// color del borde al del texto. Aquí se construye el contorno a mano para poder
// dar color semántico al borde y al icono manteniendo el texto en
// `base-content`: el amarillo de `warning` sobre base clara no llegaba a AA.
const TONE_OUTLINE = {
    neutral: "bg-transparent border border-base-content/40",
    info: "bg-transparent border border-info",
    success: "bg-transparent border border-success",
    warning: "bg-transparent border border-warning",
    error: "bg-transparent border border-error",
};

const ICON_TONE = {
    neutral: "",
    info: "text-info",
    success: "text-success",
    warning: "text-warning",
    error: "text-error",
};

const SIZE = {
    xs: "badge-xs gap-0.5",
    sm: "badge-sm gap-1",
    md: "gap-1",
    lg: "badge-lg gap-1.5",
};

export default function StatusBadge({
    label,
    tone = "neutral",
    icon = null,
    size = "sm",
    outline = false,
    className = "",
    ...rest
}) {
    const toneClass = outline
        ? TONE_OUTLINE[tone] ?? TONE_OUTLINE.neutral
        : TONE[tone] ?? TONE.neutral;

    return (
        <span
            className={`badge ${toneClass} ${SIZE[size] ?? SIZE.sm} whitespace-nowrap ${className}`.trim()}
            {...rest}
        >
            {icon && (
                <span
                    aria-hidden
                    className={`shrink-0 ${outline ? ICON_TONE[tone] ?? "" : ""}`.trim()}
                >
                    {icon}
                </span>
            )}
            {label}
        </span>
    );
}

StatusBadge.propTypes = {
    label: PropTypes.string.isRequired,
    tone: PropTypes.oneOf(["neutral", "info", "success", "warning", "error"]),
    icon: PropTypes.node,
    size: PropTypes.oneOf(["xs", "sm", "md", "lg"]),
    outline: PropTypes.bool,
    className: PropTypes.string,
};
